import './AutomaticScheduling.css';
import React, { useRef, useEffect, useState } from 'react';
import { useApiData } from '../../../../../../contexts/apiService';
import { MoonLoader } from 'react-spinners';
import { useDataManager } from "../../../../../../contexts/dataManager";
import TableInput, { InputColumnConfig } from '../../../../../../components/common/table_input/tableInput';
import SQLTable, { TableColumnConfig } from '../../../../../../components/sql_table/tableMain';
import { useUserAccess, USER_ROLE_LEVEL } from '../../../../../../contexts/userAccess';
import { getHoustonDateInfo, getUniqueDropdownOptions, getActiveProducerIds } from '../../../../../../helper_functions';
import DeleteButton from '../../../../../common/button_delete/deleteButton';

interface AutomaticSchedulingProps {
    onClose: () => void;
}

const AutomaticScheduling: React.FC<AutomaticSchedulingProps> = ({ onClose }) => {

	const { apiData, isAutomateSchedulingLoading, isCreatingLoading, userDetails } = useApiData();
	const { requestTableData, hasTableData } = useDataManager();
	const { userRole = 'viewer' } = useUserAccess();
	const panelRef = useRef<HTMLDivElement>(null);
	const [showHelp, setShowHelp] = useState(false);
    
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			// The delete-reason dialog and the column filter are portaled to document.body,
			// outside panelRef; clicks inside them must not close the panel.
			if ((event.target as Element).closest?.('.confirm-dialog-overlay, .tableFilter-overlay')) return;
			if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
				onClose();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [onClose]);

	// Producer, entity, lease and carrier are picked by id, like the Schedule Table form
	// (ScheduleTable/new/tableConfig.tsx): the scheduler copies the ids to each load. The
	// destination is still stored by station name.
	useEffect(() => {
		['producer_definition', 'entity_definition', 'lease_coordinates', 'carrier_definition', 'market_coordinates'].forEach(table => {
			if (!hasTableData(table)) requestTableData(table);
		});
	}, []);

	// Only active producers/leases are offered: getUniqueDropdownOptions already drops
	// rows whose own is_active = 0, and activeProducerIds additionally hides leases that
	// belong to an inactive producer (a lease can be active under a deactivated producer).
	const activeProducerIds = getActiveProducerIds(apiData?.producer_definition);
	const producerOptions = getUniqueDropdownOptions({
		tableData:apiData?.producer_definition,
		valueColumn: 'id',
		labelColumn: 'producer_name'
	});
	const entityOptions = getUniqueDropdownOptions({
		tableData: apiData?.entity_definition,
		valueColumn: 'id',
		labelColumn: 'entity_name',
		extraFields: ['current_producer_id']
	});
	const leaseOptions = getUniqueDropdownOptions({
			tableData: apiData?.lease_coordinates,
			valueColumn: 'id',
			labelColumn: 'lease',
			extraFields: ['producer_id', 'producer_name', 'product_type', 'region', 'entity_id']
		})
			.filter((opt: any) => activeProducerIds.has(Number(opt.producer_id)));

	const destinationOptions = getUniqueDropdownOptions({
			tableData: apiData?.market_coordinates,
			valueColumn: 'id',
			labelColumn: 'station'
		}).map((opt: any) => ({ value: opt.label, label: opt.label }));

	const carrierOptions = getUniqueDropdownOptions({
			tableData: apiData?.carrier_definition,
			valueColumn: 'id',
			labelColumn: 'name',
			extraFields: ['approved_product_types', 'operating_basin'],
		});

	const inputConfig : InputColumnConfig[] = [
		{
			colName: 'numberOfLoads',
			placeholder: 'Loads',
			colType: 'number',
			required: true,
			inputType: 'dropdown',
			dropdownOptions: Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: String(i + 1) })),
			infoTooltip: 'Loads created per scheduled day (max 10)',
		},
		{
			colName: 'producer_id',
			placeholder: 'Producer',
			colType: 'number',
			required: true,
			inputType: 'dropdown',
			dropdownOptions: producerOptions,
			linkedGroup: ['entity_id', 'lease_id'],
			getLinkedFilteredOptions: (g: Record<string, any>, allProducers: any[]) => {
				if (g.entity_id) {
					const entity = entityOptions.find(e => Number(e.value) === Number(g.entity_id));
					if (entity?.current_producer_id)
						return allProducers.filter(p => Number(p.value) === Number(entity.current_producer_id));
				}
				if (g.lease_id) {
					const lease = leaseOptions.find(l => Number(l.value) === Number(g.lease_id));
					if (lease?.entity_id) {
						const entity = entityOptions.find(e => Number(e.value) === Number(lease.entity_id));
						if (entity?.current_producer_id)
							return allProducers.filter(p => Number(p.value) === Number(entity.current_producer_id));
					}
				}
				return allProducers;
			}
		},
		{
			colName: 'entity_id',
			placeholder: 'Entity',
			colType: 'number',
			required: true,
			inputType: 'dropdown',
			dropdownOptions: entityOptions,
			linkedGroup: ['producer_id', 'lease_id'],
			getLinkedFilteredOptions: (g: Record<string, any>, allEntities: any[]) => {
				if (g.lease_id) {
					const lease = leaseOptions.find(l => Number(l.value) === Number(g.lease_id));
					if (lease?.entity_id)
						return allEntities.filter(e => Number(e.value) === Number(lease.entity_id));
				}
				if (g.producer_id) {
					return allEntities.filter(e => Number(e.current_producer_id) === Number(g.producer_id));
				}
				return allEntities;
			}
		},
		{
			colName: 'lease_id',
			placeholder: 'Lease',
			colType: 'number',
			required: true,
			inputType: 'dropdown',
			dropdownOptions: leaseOptions,
			linkedGroup: ['producer_id', 'entity_id'],
			getLinkedFilteredOptions: (g: Record<string, any>, allLeases: any[]) => {
				if (g.entity_id)
					return allLeases.filter(l => Number(l.entity_id) === Number(g.entity_id));
				if (g.producer_id)
					return allLeases.filter(l => Number(l.producer_id) === Number(g.producer_id));
				return allLeases;
			}
		},
		{
			colName: 'frequency',
			colType: 'number',
			required: true,
			placeholder: ' ',
			infoTooltip: 'How often the rule repeats, in days (1 = every day)',
		},
		{
			colName: 'startDate',
			placeholder: getHoustonDateInfo().todayString,
			colType: 'date',
			inputType: 'date',
			required: true,
			infoTooltip: 'First day the rule creates loads',
		},
		{
			colName: 'endDate',
			placeholder: getHoustonDateInfo().todayString,
			colType: 'date',
			inputType: 'date',
			required: true,
			infoTooltip: 'Last day the rule creates loads (inclusive)',
		},
		{
			colName: 'destination',
			placeholder: 'Destination',
			colType: 'text',
			inputType: 'dropdown',
			dropdownOptions: destinationOptions || [],
		},
		{
			colName: 'requestMethod',
			placeholder: 'Request Method',
			colType: 'text',
			inputType: 'dropdown',
			dropdownOptions: [
				{ value: 'Text', label: 'Text' },
				{ value: 'Call', label: 'Call' },
				{ value: 'Email', label: 'Email' },
				{ value: 'Other', label: 'Other' },
			],
		},
		{
			colName: 'carrier_id',
			placeholder: 'Carrier',
			colType: 'number',
			inputType: 'dropdown',
			dropdownOptions: carrierOptions,
		},
		{
			colName: 'requestDate',
			placeholder: getHoustonDateInfo().todayString,
			colType: 'datetime',
			inputType: 'datetime',
			required: true,
			infoTooltip: 'Date * - Time request was made *',
		},
		{
			colName: 'requestMadeBy',
			placeholder: 'Requested By',
			colType: 'text',
			inputType: 'dropdown',
			dropdownOptions: userDetails?.name ? [{ value: userDetails.name, label: userDetails.name }] : [],
			// Auto-fill the creator with the current user so nobody has to pick it.
			defaultValue: userDetails?.name || '',
			required: true,
		},
	]
	
	// No "Active" column: the [startDate, endDate] window — not a manual toggle — is what
	// starts and stops a rule. Showing "Active = Yes" on a rule whose window has already
	// passed is misleading, so to stop a rule early you shorten its End Date instead.
	// Editing either date runs the same immediate generation as saving a rule (web_edit_row).
	const colConfig : TableColumnConfig[] = [
		{
			colName: 'producer',
			colHeader: 'Producer',
			colType: 'text',
			editable: false,
		},
		{
			colName: 'lease',
			colHeader: 'Lease',
			colType: 'text',
			editable: false,
		},
		{
			colName: 'carrier',
			colHeader: 'Carrier',
			colType: 'text',
			editable: false,
		},
		{
			colName: 'destination',
			colHeader: 'Destination',
			colType: 'text',
			editable: false,
		},
		{
			colName: 'requestDate',
			colHeader: 'Requested Date',
			colType: 'datetime',
			editable: false,
		},
		{
			colName: 'requestMethod',
			colHeader: 'Method',
			colType: 'text',
			editable: false,
		},
		{
			colName: 'requestMadeBy',
			colHeader: 'Scheduler',
			colType: 'text',
			editable: false,
		},
		{
			colName: 'numberOfLoads',
			colHeader: 'Loads',
			colType: 'number',
			editable: false,
		},
		{
			colName: 'frequency',
			colHeader: 'Frequency',
			colType: 'text',
			editable: false,
			conditionalDisplay: (value: any) => value ? `Every ${value} days` : '',
		},
		{
			colName: 'startDate',
			colHeader: 'Start Date',
			colType: 'date',
			editable: true,
		},
		{
			colName: 'endDate',
			colHeader: 'End Date',
			colType: 'date',
			editable: true,
		},
	]
		
	return (
		<div className="as-panel-overlay">
			<div className="overlay" onClick={onClose}>
				<div className="automaticScheduling-panel" onClick={(e) => e.stopPropagation()}>
					<div className="as-panel-container" ref={panelRef}>
						<div className="as-header">
							<div className="as-header-titles">
								<h2>Automatic Scheduling</h2>
								<p className="as-subtitle">Rules that schedule loads automatically within a date range.</p>
							</div>
							<DeleteButton
								onClick={onClose}
								text="Close"
							/>
						</div>

						<button
							type="button"
							className={`as-help-toggle ${showHelp ? 'open' : ''}`}
							onClick={() => setShowHelp((v) => !v)}
							aria-expanded={showHelp}
						>
							<span className="as-help-icon" aria-hidden="true">i</span>
							<span>How it works</span>
							<span className="as-help-chevron" aria-hidden="true">▾</span>
						</button>

						{showHelp && (
							<div className="as-help">
								<ul className="as-help-list">
									<li>
										<strong>Loads appear a day early (lead time).</strong> The scheduler runs{' '}
										<strong>once a day at 7:00&nbsp;AM Texas time (US&nbsp;Central)</strong>. Each run
										creates the <em>next</em> day&apos;s load, so a load scheduled for a date shows up
										on the table <strong>the day before</strong> — giving you time to coordinate the
										carrier. (A load for 10/20 appears on the table on 10/19 at 7:00&nbsp;AM.)
									</li>
									<li>
										<strong>Near-term loads are created immediately.</strong> When you save a rule,
										any load due <strong>today or tomorrow</strong> (less than 2 days out) is created
										right away — you don&apos;t wait for the next run. So a rule that starts today puts
										today&apos;s and tomorrow&apos;s loads on the table the moment you save it.
									</li>
									<li>
										<strong>Later loads fill in day by day.</strong> The rest of the loads in the
										range are not created all at once — the scheduler adds each one automatically,
										one day ahead of its date.
									</li>
									<li>
										<strong>Date window.</strong> Pick a <strong>Start Date</strong> and{' '}
										<strong>End Date</strong>; the rule creates loads automatically between those
										dates and stops on its own at the End Date — no turning it on and off by hand.
									</li>
									<li>
										<strong>Changing the dates.</strong> Click a Start or End Date in the table to edit
										it (format <strong>YYYY-MM-DD</strong>). If the new range includes today or
										tomorrow, those loads are created right away, just like when you save a rule.
										To stop a rule early, move its End Date back. Loads already on the table are
										never removed or recreated — edit or cancel them in the Schedule Table.
									</li>
								</ul>
							</div>
						)}

						<h3 className="as-section-label">Add a rule</h3>
						<div className="as-dropdown-section">
							<TableInput
								tableName="automate_scheduling"
								inputColumnConfigs={inputConfig}
							/>
						</div>

						{(isAutomateSchedulingLoading || isCreatingLoading) && (
							<div className="as-loading-overlay">
								<MoonLoader size={35} color="#2973A6" />
							</div>
						)}

						<div onClick={(e) => e.stopPropagation()}>
							<SQLTable
								tableData={apiData?.automate_scheduling || []}
								tableName="automate_scheduling"
								idColumn="id"
								columnConfigs={colConfig}
								deletableRows={true}
								deletionRequiresApproval={true}
								selectableRows={true}
								enableFilters={true}
								showHeader={true}
								showTable={true}
								sortOrder={{ 'requestDate': 'desc' }}
								userRoleLevel={USER_ROLE_LEVEL[userRole] || 1}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AutomaticScheduling;
